import React from 'react';
import { Badge, ButtonText, OutlineCard, Table } from '@morressier/alembic';
import { Flex, Text, Box, Spinner, Button, Tooltip } from '@chakra-ui/react';
import {
  faArrowLeft,
  faArrowUp,
  faFile
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import { fetchEmailByEventId } from '../../../api/queries';
import { EmailHistory } from '@publishing-engine/domain/models/email-templates';
import {
  IActivityLog,
  IRoleType,
  allActivityLogTypes
} from '@publishing-engine/domain/models';

type EmailEnvelopeData = {
  subject?: string;
  body?: string;
  recipients?: {
    to?: string[];
    cc?: string[];
    bcc?: string[];
  };
  attachments?: Array<{
    name: string;
    url: string;
    size?: number;
  }>;
};

type SendgridDeliveryEvent = {
  _id?: string;
  eventType?: string;
  email?: string;
  timestamp?: string | Date;
  sg_event_id?: string;
  sg_message_id?: string;
  trackerId?: string;
  eventData?: {
    reason?: string;
    status?: string;
    response?: string;
    attemptCount?: number;
    attempt?: number;
    smtpId?: string;
    url?: string;
    userAgent?: string;
    originalReason?: string;
    bounceType?: string;
    [key: string]: unknown;
  };
  metadata?: {
    ip?: string;
    userAgent?: string;
    category?: string[];
    asmGroupId?: number;
    customArgs?: Record<string, unknown>;
  };
  createdAt?: string | Date;
  updatedAt?: string | Date;
};

type EmailPreviewLog = Partial<EmailHistory> & {
  _id?: string;
  sendgridDetails?: SendgridDeliveryEvent[];
};

import { Overlay } from '../../Overlay';
import RichTextPreview from '../GenericFieldComponents/RichText/RichTextPreview';
import { removeHrefKeepText, renderRecipient } from 'src/utils/email.utils';
import { formatDateAndTime } from 'src/utils/date';
import NavbarDashboard from 'src/components/NavbarDashboard';
import FileViewerButtonText from '../FileViewerButtonText';

const EMPTY_VALUE = '--';
const BACK_BUTTON_STICKY_TOP = 56;
const EMAIL_DETAIL_SCROLL_OFFSET = 132;

const getContentTitle = (type: allActivityLogTypes): string => {
  switch (type) {
    case 'SubmissionNote':
      return 'Note';
    case 'EarlyAssessmentRequested':
      return 'Task to Perform';
    case 'AdjudicationRequested':
      return 'Adjudication Comment';
    case 'EditorialImmediateRevisionRequested':
      return 'Editor Comment on Immediate Revision';
    case 'DecisionReverted':
      return 'Reason for Decision Reversal';
    case 'EditorialChangeRequestSubmitted':
      return 'Reason for Change Request';
    case 'RevisionRequested':
      return 'Revision Instructions';
    case 'EditorialSubmissionWithdrawn':
      return 'Reason for Withdrawal';
    case 'AuthorSubmissionWithdrawalRequested':
      return 'Reason for Withdrawal Request';
    case 'PeerReviewInterrupted':
      return 'Reason for Interruption';
    default:
      return 'Details';
  }
};

interface EmailViewModalProps {
  onClose: () => void;
  submissionId: string;
  event: IActivityLog & { eventId: string };
  submissionContextId: string;
  roleContext: IRoleType;
  reviewId?: string;
}

const createEmailDataFromEvent = (
  event: IActivityLog & { eventId: string }
): EmailPreviewLog[] => {
  const reviewerEmailData =
    typeof event.reviewerEmail === 'object'
      ? (event.reviewerEmail as EmailEnvelopeData)
      : null;

  const expertEmailData =
    typeof event.expertEmail === 'object'
      ? (event.expertEmail as EmailEnvelopeData)
      : null;

  const emailData = expertEmailData || reviewerEmailData;

  const getAttachments = () => {
    const emailAttachments = emailData?.attachments || [];
    const editorialAttachments = event.editorialRequest?.attachments || [];
    const eventAttachments = event.attachments || [];

    return [...emailAttachments, ...editorialAttachments, ...eventAttachments];
  };

  return [
    {
      _id: event.eventId,
      subject: emailData?.subject || event.content || 'Ad Hoc Email',
      body: emailData?.body || event.content || '',
      recipients: {
        to: emailData?.recipients?.to || [
          typeof event.reviewerEmail === 'string'
            ? event.reviewerEmail
            : typeof event.expertEmail === 'string'
            ? event.expertEmail
            : ''
        ],
        cc: emailData?.recipients?.cc,
        bcc: emailData?.recipients?.bcc
      },
      attachments: getAttachments()
    }
  ];
};

export const Preview = (props: {
  emailLog?: EmailPreviewLog[];
  fallbackEvent?: IActivityLog & { eventId: string };
  showNavigation?: boolean;
  submissionContextId?: string;
}) => {
  // Use API data if available, otherwise fall back to Activity Log data
  const dataToShow =
    props.emailLog && props.emailLog.length > 0
      ? props.emailLog
      : props.fallbackEvent
      ? createEmailDataFromEvent(props.fallbackEvent)
      : [];
  const [activeEmailIndex, setActiveEmailIndex] = React.useState(0);

  React.useEffect(() => {
    if (!props.showNavigation || dataToShow.length <= 1) return;

    const updateActiveEmail = () => {
      const nextActiveIndex = dataToShow.reduce((activeIndex, _log, index) => {
        const section = document.getElementById(getEmailDetailSectionId(index));

        if (!section) return activeIndex;

        return section.getBoundingClientRect().top <= EMAIL_DETAIL_SCROLL_OFFSET
          ? index
          : activeIndex;
      }, 0);

      setActiveEmailIndex(nextActiveIndex);
    };

    updateActiveEmail();
    window.addEventListener('scroll', updateActiveEmail, { passive: true });
    window.addEventListener('resize', updateActiveEmail);

    return () => {
      window.removeEventListener('scroll', updateActiveEmail);
      window.removeEventListener('resize', updateActiveEmail);
    };
  }, [dataToShow.length, props.showNavigation]);

  if (props.emailLog && props.emailLog.length === 0) {
    return <MailSendingState />;
  }

  if (!dataToShow || dataToShow.length === 0) return <Text>No data</Text>;

  const scrollToEmailDetail = (
    event: React.MouseEvent<HTMLDivElement>,
    index: number
  ) => {
    event.preventDefault();
    setActiveEmailIndex(index);

    document.getElementById(getEmailDetailSectionId(index))?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  const renderedEmailDetails = dataToShow.map((log, index) => {
    const sendgridDetails = log.sendgridDetails ?? [];
    const sectionId = getEmailDetailSectionId(index);

    return (
      <Box
        id={sectionId}
        key={log._id ?? log.eventId ?? sectionId}
        scrollMarginTop={`${EMAIL_DETAIL_SCROLL_OFFSET}px`}
      >
        <OutlineCard gap={6} p={4} mb={10} width="100%">
          <Box backgroundColor="white" p={6} borderRadius="small">
            <Flex direction="column" gap={4}>
              <Flex direction="column" gap={1}>
                <EmailRecipientRow label="To">
                  {renderRecipient(log.recipients?.to) || EMPTY_VALUE}
                </EmailRecipientRow>
                <EmailRecipientRow label="CC">
                  {renderRecipient(log.recipients?.cc) || EMPTY_VALUE}
                </EmailRecipientRow>
                <EmailRecipientRow label="BCC">
                  {renderRecipient(log.recipients?.bcc) || EMPTY_VALUE}
                </EmailRecipientRow>
              </Flex>

              <Flex direction="column" gap={2}>
                <Text textStyle="headingItem">Subject</Text>
                <Text textStyle="textBody" maxWidth="680px">
                  {log?.subject || EMPTY_VALUE}
                </Text>
                <Text textStyle="headingItem">Body Text</Text>
                {log?.body ? (
                  <RichTextPreview value={removeHrefKeepText(log.body) ?? ''} />
                ) : (
                  <Text textStyle="textBody">{EMPTY_VALUE}</Text>
                )}
              </Flex>

              {log.attachments && log.attachments.length > 0 && (
                <Flex flexDirection="column" gap={2}>
                  <Text textStyle="headingItem">Attached Files</Text>
                  {log.attachments.map(
                    (
                      attachment: { name: string; url: string },
                      index: number
                    ) => (
                      <Flex justifyContent="space-between" key={index}>
                        <Flex gap={2} alignItems="center">
                          <FontAwesomeIcon icon={faFile} />
                          <Text textStyle="textBody">{attachment.name}</Text>
                          <FileViewerButtonText
                            file={attachment}
                            submissionContextId={props.submissionContextId}
                          >
                            View
                          </FileViewerButtonText>
                        </Flex>
                      </Flex>
                    )
                  )}
                </Flex>
              )}
            </Flex>
          </Box>

          <Box as="hr" borderColor="borderPrimary" width="100%" />

          <Box backgroundColor="white" p={6} borderRadius="small">
            <Flex direction="column" gap={5}>
              <Text textStyle="headingItem">Delivery Details</Text>

              {sendgridDetails.length > 0 ? (
                <DeliveryDetailsTable details={sendgridDetails} />
              ) : (
                <Text textStyle="textBody">No delivery logs found.</Text>
              )}
            </Flex>
          </Box>
        </OutlineCard>
      </Box>
    );
  });

  if (!props.showNavigation || dataToShow.length <= 1) {
    return <>{renderedEmailDetails}</>;
  }

  return (
    <Flex
      gap={6}
      alignItems="flex-start"
      direction={{ base: 'column', lg: 'row' }}
    >
      <Box
        as="nav"
        backgroundColor="white"
        borderRadius="small"
        p={4}
        width={{ base: '100%', lg: '240px' }}
        flexShrink={0}
        position={{ base: 'static', lg: 'sticky' }}
        top={`${EMAIL_DETAIL_SCROLL_OFFSET}px`}
        maxHeight={{
          base: '320px',
          lg: `calc(100vh - ${EMAIL_DETAIL_SCROLL_OFFSET + 24}px)`
        }}
        display="flex"
        flexDirection="column"
      >
        <Text textStyle="headingItem" mb={3} flexShrink={0}>
          Email Details
        </Text>
        <Flex direction="column" gap={2} overflowY="auto" pr={1}>
          {dataToShow.map((log, index) => (
            <EmailDetailNavItem
              key={log._id ?? log.eventId ?? index}
              log={log}
              index={index}
              isActive={activeEmailIndex === index}
              onClick={scrollToEmailDetail}
            />
          ))}
        </Flex>
      </Box>
      <Box flex={1} minWidth="0" width="100%">
        {renderedEmailDetails}
      </Box>
    </Flex>
  );
};

const getEmailDetailSectionId = (index: number) => `email-detail-${index + 1}`;

const EmailDetailNavItem = ({
  log,
  index,
  isActive,
  onClick
}: {
  log: EmailPreviewLog;
  index: number;
  isActive: boolean;
  onClick: (event: React.MouseEvent<HTMLDivElement>, index: number) => void;
}) => (
  <Box
    onClick={(event: React.MouseEvent<HTMLDivElement>) => onClick(event, index)}
    display="block"
    cursor="pointer"
    border="1px solid"
    borderColor={isActive ? 'actionPrimaryFill' : 'borderPrimary'}
    borderRadius="small"
    backgroundColor={isActive ? 'surfaceSecondaryFill' : 'white'}
    p={3}
    textDecoration="none"
    _hover={{ backgroundColor: 'surfaceSecondaryFill' }}
  >
    <Text textStyle="headingItem">Email {index + 1}</Text>
    <Text textStyle="textContext" mt={1} noOfLines={1}>
      {renderRecipient(log.recipients?.to) || EMPTY_VALUE}
    </Text>
    <Text textStyle="textContext" mt={1} noOfLines={2}>
      {log.subject || EMPTY_VALUE}
    </Text>
  </Box>
);

const MailSendingState = () => (
  <OutlineCard gap={4} p={8} mb={10}>
    <Flex
      direction="column"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      gap={4}
      minHeight="220px"
    >
      <Flex direction="column" gap={2}>
        <Text textStyle="textBody" maxWidth="520px">
          If an email template is set, your email content will be displayed here
          shortly.
        </Text>
      </Flex>
    </Flex>
  </OutlineCard>
);

const DeliveryDetailsTable = ({
  details
}: {
  details: SendgridDeliveryEvent[];
}) => (
  <Table>
    <Table.HeaderRow gap={4}>
      <DeliveryHeaderCell flex="2">Recipient Email</DeliveryHeaderCell>
      <DeliveryHeaderCell flex="1">Event Type</DeliveryHeaderCell>
      <DeliveryHeaderCell flex="1.4">Reason</DeliveryHeaderCell>
      <DeliveryHeaderCell flex="1.6">Event Time</DeliveryHeaderCell>
    </Table.HeaderRow>
    <Table.Body>
      {details.map((detail, index) => (
        <Table.Row
          key={detail._id ?? detail.sg_event_id ?? index}
          gap={4}
          alignItems="flex-start"
          _last={{ borderBottomWidth: 0 }}
        >
          <DeliveryTextCell flex="2" value={formatDetailValue(detail.email)} />
          <DeliveryBadgeCell flex="1" value={detail.eventType} />
          <DeliveryTextCell
            flex="1.4"
            value={formatDetailValue(detail.eventData?.reason)}
          />
          <DeliveryTextCell
            flex="1.6"
            value={formatDeliveryDate(detail.timestamp)}
          />
        </Table.Row>
      ))}
    </Table.Body>
  </Table>
);

const DeliveryHeaderCell = ({
  flex,
  children
}: {
  flex: string;
  children: React.ReactNode;
}) => (
  <Table.HeaderCell flex={flex} minWidth="0" wordBreak="break-word">
    {children}
  </Table.HeaderCell>
);

const DeliveryTextCell = ({ flex, value }: { flex: string; value: string }) => (
  <Table.RowCell
    flex={flex}
    minWidth="0"
    textStyle="textBody"
    textAlign="left"
    whiteSpace="pre-wrap"
    wordBreak="break-word"
  >
    {value}
  </Table.RowCell>
);

const DeliveryBadgeCell = ({
  flex,
  value
}: {
  flex: string;
  value: unknown;
}) => {
  const formattedValue = formatDetailValue(value);

  return (
    <Table.RowCell flex={flex} minWidth="0" textAlign="left">
      {formattedValue === EMPTY_VALUE ? (
        EMPTY_VALUE
      ) : (
        <Badge
          size="context"
          variant={formattedValue === 'delivered' ? 'success' : 'caution'}
        >
          {formattedValue}
        </Badge>
      )}
    </Table.RowCell>
  );
};

export const EmailRecipientRow = ({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Flex gap={6} alignItems="flex-start" minWidth="0">
    <Text textStyle="headingItem" width="44px" flexShrink={0}>
      {label}
    </Text>
    <Text textStyle="textBody" whiteSpace="pre-wrap" wordBreak="break-word">
      {children}
    </Text>
  </Flex>
);

const formatDeliveryDate = (value?: string | Date) => {
  if (!value) return EMPTY_VALUE;

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? EMPTY_VALUE : formatDateAndTime(date);
};

const formatDetailValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  if (Array.isArray(value)) return value.join(', ') || EMPTY_VALUE;
  if (typeof value === 'object') return JSON.stringify(value);

  return String(value);
};

const formatEmailDetailsCount = (count: number) =>
  `${count} email detail${count === 1 ? '' : 's'}`;

const EmailScrollToTopButton = () => {
  const scrollToFirstEmail = () => {
    document.getElementById(getEmailDetailSectionId(0))?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  return (
    <Tooltip
      label="Scroll to first email"
      aria-label="Scroll to first email"
      hasArrow
      borderRadius={4}
      placement="top"
    >
      <Button
        onClick={scrollToFirstEmail}
        position="fixed"
        bottom="75px"
        right="40px"
        zIndex={1000}
        size="sm"
        variant="primary"
        borderRadius="full"
        p={0}
        w="40px"
        h="40px"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <FontAwesomeIcon icon={faArrowUp} size="lg" />
      </Button>
    </Tooltip>
  );
};

const EmailViewModal: React.FC<EmailViewModalProps> = ({
  onClose,
  event,
  submissionId,
  submissionContextId,
  roleContext,
  reviewId
}) => {
  const { eventId } = event;
  const {
    data: emailLog,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['email-by-event', eventId, roleContext, reviewId],
    queryFn: () =>
      fetchEmailByEventId(
        submissionId,
        eventId,
        roleContext,
        undefined,
        reviewId
      ),
    enabled: !!eventId
  });
  const emailDetailsCount =
    emailLog?.items?.length ?? createEmailDataFromEvent(event).length;
  const emailDetailsCountText =
    emailDetailsCount > 0
      ? ` (${formatEmailDetailsCount(emailDetailsCount)})`
      : '';

  return (
    <Overlay id="scrollable-wrapper">
      <NavbarDashboard submissionContextId={submissionContextId} />
      <Flex
        minHeight={'full'}
        direction="column"
        backgroundColor="surfaceSecondaryFill"
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          p={6}
          position="sticky"
          top={`${BACK_BUTTON_STICKY_TOP}px`}
          zIndex={900}
          backgroundColor="surfaceSecondaryFill"
        >
          <ButtonText
            variant="secondary"
            onClick={onClose}
            iconLeft={<FontAwesomeIcon icon={faArrowLeft} />}
          >
            Back to Activity
          </ButtonText>
        </Flex>
        <EmailScrollToTopButton />
        <Box flex={1} p={4} pt={0} width="100%" maxWidth="1140px" mx="auto">
          <Text textStyle="headingSection">
            Submission associated with {event.userFirstName}{' '}
            {event.userLastName}
            {emailDetailsCountText}
          </Text>

          <Flex gap={4} mt={2} mb={6}>
            <Text textStyle="textBody">
              By {event.userFirstName && event.userFirstName}{' '}
              {event.userLastName && event.userLastName}
            </Text>
            <Text textStyle="textBody">
              {formatDateAndTime(new Date(event.date))}
            </Text>
          </Flex>
          {(event.content ||
            (event.type === 'EarlyAssessmentRequested' &&
              typeof event.expertEmail === 'object' &&
              event.expertEmail?.body) ||
            (event.type === 'AdjudicationRequested' &&
              typeof event.expertEmail === 'object' &&
              event.expertEmail?.body) ||
            event.editorialRequest?.comment) && (
            <Box backgroundColor="white" p={6} borderRadius="small" mb={10}>
              <Text textStyle="headingItem" mb={2}>
                {getContentTitle(event.type as allActivityLogTypes)}
              </Text>
              <Text textStyle="textBody">
                {event.content ||
                  (event.type === 'EarlyAssessmentRequested' &&
                    ((typeof event.expertEmail === 'object' &&
                      event.expertEmail?.body) ||
                      event.editorialRequest?.comment)) ||
                  (event.type === 'AdjudicationRequested' &&
                    ((typeof event.expertEmail === 'object' &&
                      event.expertEmail?.body) ||
                      event.editorialRequest?.comment)) ||
                  ''}
              </Text>
            </Box>
          )}
          {isLoading ? (
            <Flex alignItems="center" justifyContent="center" height="100%">
              <Spinner />
            </Flex>
          ) : isError ? (
            <Text color="red.500">
              Error loading email:{' '}
              {error instanceof Error ? error.message : 'Unknown error'}
            </Text>
          ) : (
            <Preview
              emailLog={emailLog?.items}
              fallbackEvent={event}
              showNavigation
              submissionContextId={submissionContextId}
            />
          )}
        </Box>
      </Flex>
    </Overlay>
  );
};

export default EmailViewModal;
